import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

// Configure axios to use admin server proxy (not direct backend)
// The admin server will proxy requests to the backend
// No need to set baseURL since we're using the proxy configuration

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Remove axios authentication headers since backend has no auth
  useEffect(() => {
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check with server for current user session
        const response = await axios.get('/api/auth/user');
        setUser(response.data.user);
      } catch (error) {
        // No valid session, user not authenticated
        setUser(null);
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email, password, captchaToken) => {
    try {
      const response = await axios.post('/api/auth/login', {
        email,
        password,
        captchaToken
      });

      setUser(response.data.user);
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed',
        hasOAuth: error.response?.data?.hasOAuth
      };
    }
  };

  const loginWithOAuth = async (userData) => {
    try {
      const response = await axios.post('/api/auth/oauth', {
        email: userData.email,
        name: userData.name,
        provider: userData.provider,
        oauth_id: userData.id
      });

      setUser(response.data.user);
      return { success: true, user: response.data.user };
    } catch (error) {
      console.error('OAuth login error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'OAuth authentication failed'
      };
    }
  };

  const register = async (email, password, name, captchaToken) => {
    try {
      const response = await axios.post('/api/auth/register', {
        email,
        password,
        name,
        captchaToken
      });

      setUser(response.data.user);
      return { success: true, user: response.data.user };
    } catch (error) {
      console.error('Registration error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Registration failed',
        hasOAuth: error.response?.data?.hasOAuth,
        provider: error.response?.data?.provider,
        userId: error.response?.data?.userId
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
  };

  const value = {
    user,
    login,
    loginWithOAuth,
    register,
    logout,
    loading,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
